import React from 'react';

import {
    SelectTextField as DSSelectTextField,
    type MultipleSelectTextFieldProps as DSMultipleSelectTextFieldProps,
    type SelectTextFieldTranslations,
    type SingleSelectTextFieldProps as DSSingleSelectTextFieldProps,
} from '@lumx/react';

import { useSelectTextFieldTranslations } from '../../hooks/useSelectTextFieldTranslations';

/** Make the DS `translations` optional (merged over the LumApps defaults). */
type WithOptionalTranslations<P> = Omit<P, 'translations'> & {
    /** Override some (or all) of the default translations. */
    translations?: Partial<SelectTextFieldTranslations>;
};

/** Single selection props */
export type SingleSelectTextFieldProps<O = any> = WithOptionalTranslations<DSSingleSelectTextFieldProps<O>>;

/** Multiple selection props */
export type MultipleSelectTextFieldProps<O = any> = WithOptionalTranslations<DSMultipleSelectTextFieldProps<O>>;

/** SelectTextField props (discriminated on `selectionType`) */
export type SelectTextFieldProps<O = any> = SingleSelectTextFieldProps<O> | MultipleSelectTextFieldProps<O>;

/**
 * A text field with a select dropdown to choose between a list of options.
 * Supports search/filter, single selection, and multiple selection with chips.
 */
const InnerSelectTextField = <O,>({ translations, ...props }: SelectTextFieldProps<O>) => {
    const defaultTranslations = useSelectTextFieldTranslations(props.label);
    const mergedTranslations = React.useMemo(
        () => ({ ...defaultTranslations, ...translations }),
        [defaultTranslations, translations],
    );

    return <DSSelectTextField<O> {...(props as DSSingleSelectTextFieldProps<O>)} translations={mergedTranslations} />;
};
InnerSelectTextField.displayName = 'SelectTextField';

/**
 * One overload per selection type, so the option type `O` is inferred from the matching branch only
 * (inference on the `Omit`-ed union can pick `value?: O` of the single branch for a multiple select).
 */
type SelectTextFieldComponent = {
    <O>(props: SingleSelectTextFieldProps<O>): React.JSX.Element;
    <O>(props: MultipleSelectTextFieldProps<O>): React.JSX.Element;
    /** Fallback for props typed as the union itself. */
    <O>(props: SelectTextFieldProps<O>): React.JSX.Element;
    displayName?: string;
};

/**
 * A text field with a select dropdown to choose between a list of options.
 * Supports search/filter, single selection, and multiple selection with chips.
 *
 * Adds opinionated default `translations` (each one can be overridden with the `translations` prop).
 *
 * @family Inputs
 */
export const SelectTextField = Object.assign(InnerSelectTextField as SelectTextFieldComponent, {
    Option: DSSelectTextField.Option,
    Section: DSSelectTextField.Section,
    OptionMoreInfo: DSSelectTextField.OptionMoreInfo,
    OptionSkeleton: DSSelectTextField.OptionSkeleton,
    Divider: DSSelectTextField.Divider,
});
